<script setup lang="ts">
import { computed } from 'vue'
import { LayerItemMapLayer } from '@/components/layers'

// Render-only layer list: draws a map's org-layers onto a specific Mapbox
// instance WITHOUT any sidebar row UI or drag monitors. Layer ids are namespaced
// per map (`...__t<mapId>`) so z-layer groups never collide across instances.

const props = defineProps<{
  map: any            // the target mapboxgl.Map instance
  layers: any[]       // resolved layer objects (from maps/getMapLayers)
  orgId: number | string
  mapId: string
  ready: boolean
}>()

const DATA_TYPE_GROUP = 3
const orgIdNum = computed(() => Number(props.orgId))

const ns = (uuid: string) => `layer-${uuid}__t${props.mapId}`

// Flat z-position map mirroring LayerList: header at P, its children at P+1, P+2…
const flat = computed(() => {
  const m = new Map<string, number>()
  let pos = 0
  for (const layer of props.layers) {
    m.set(layer.uuid, pos++)
    if (layer.properties?.data_type === DATA_TYPE_GROUP) {
      for (const child of (layer.properties?.children || [])) m.set(child.uuid, pos++)
    }
  }
  return m
})

const isVisible = (layer: any) => layer.properties?.visible ?? true
</script>

<template>
  <template v-if="ready">
    <template v-for="layer in layers" :key="layer.uuid">
      <LayerItemMapLayer
        :map="map"
        :item="layer"
        :layer-id="ns(layer.uuid)"
        :position="flat.get(layer.uuid) ?? 0"
        :org-id="orgIdNum"
        :is-visible="isVisible(layer)"
      />
      <template v-if="layer.properties?.data_type === DATA_TYPE_GROUP">
        <LayerItemMapLayer
          v-for="(child, childIndex) in (layer.properties?.children || [])"
          :key="child.uuid"
          :map="map"
          :item="child"
          :layer-id="ns(child.uuid)"
          :position="(flat.get(layer.uuid) ?? 0) + 1 + Number(childIndex)"
          :org-id="orgIdNum"
          :is-visible="isVisible(layer) && (child.properties?.visible ?? true)"
        />
      </template>
    </template>
  </template>
</template>
